import { ApiSearchResult } from '../api-types/search.api';
import { PERSON_TYPE, SearchResultItem } from '../app-types/search.app';
import { tranformApiContactPointToAppContactPoint } from './contact-point.transform';
import { transformApiInstitutionBranchItemToAppInstitutionBranchItem } from './instition.tranform';

/**
 * Transforms an array of API search results to application search results.
 * Filters out incomplete results that cannot be rendered safely.
 * @param input The array of API search results to transform.
 * @returns The array of transformed application search results.
 */
export const transformApiSearchResultToAppSearchResult = (input: ApiSearchResult[]): SearchResultItem[] => {
	return input.filter(isValidSearchResult).map((item) => transformItemBasedOnPersonType(item));
};

/**
 * Returns true when the search result contains the minimum data required by the UI.
 * @param item The API search result item to validate.
 * @returns True when the result can be rendered safely.
 */
const isValidSearchResult = (item: ApiSearchResult): boolean => {
	return !!item?.item && item.personType != null;
};

/**
 * Transforms an API search result item to an application search result item.
 * @param item The API search result item to transform.
 * @returns The transformed application search result item.
 */
const transformItemBasedOnPersonType = (itemApi: ApiSearchResult): SearchResultItem => {
	switch (itemApi.personType) {
		case PERSON_TYPE.CLIENT:
			return {
				item: itemApi.item,
				personType: PERSON_TYPE.CLIENT,
				ableToRepresentAs: itemApi.ableToRepresentAs,
				contactPoints: (itemApi.contactPoints ?? []).map(tranformApiContactPointToAppContactPoint),
				isInYourIntegrationServiceOffice: itemApi.isInYourIntegrationServiceOffice,
				institutionBranch: itemApi.institutionBranch
					? transformApiInstitutionBranchItemToAppInstitutionBranchItem(itemApi.institutionBranch)
					: undefined,
			};
		case PERSON_TYPE.USER:
			return {
				item: itemApi.item,
				personType: PERSON_TYPE.USER,
				ableToRepresentAs: itemApi.ableToRepresentAs,
				contactPoints: (itemApi.contactPoints ?? []).map(tranformApiContactPointToAppContactPoint),
				isInYourIntegrationServiceOffice: itemApi.isInYourIntegrationServiceOffice,
				institutionBranch: itemApi.institutionBranch
					? transformApiInstitutionBranchItemToAppInstitutionBranchItem(itemApi.institutionBranch)
					: undefined,
			};
		case PERSON_TYPE.INSTITUTION:
			return {
				item: itemApi.item,
				personType: PERSON_TYPE.INSTITUTION,
				ableToRepresentAs: itemApi.ableToRepresentAs,
				isInYourIntegrationServiceOffice: itemApi.isInYourIntegrationServiceOffice,
				institutionBranch: itemApi.institutionBranch
					? transformApiInstitutionBranchItemToAppInstitutionBranchItem(itemApi.institutionBranch)
					: undefined,
			};
		case PERSON_TYPE.RELATIVES:
			return {
				item: itemApi.item,
				personType: PERSON_TYPE.RELATIVES,
				ableToRepresentAs: itemApi.ableToRepresentAs,
				contactPoints: (itemApi.contactPoints ?? []).map(tranformApiContactPointToAppContactPoint),
				isInYourIntegrationServiceOffice: itemApi.isInYourIntegrationServiceOffice,
				institutionBranch: itemApi.institutionBranch
					? transformApiInstitutionBranchItemToAppInstitutionBranchItem(itemApi.institutionBranch)
					: undefined,
			};
		case PERSON_TYPE.BIH:
			return {
				item: itemApi.item,
				personType: PERSON_TYPE.BIH,
				ableToRepresentAs: itemApi.ableToRepresentAs,
				isInYourIntegrationServiceOffice: itemApi.isInYourIntegrationServiceOffice,
				institutionBranch: itemApi.institutionBranch
					? transformApiInstitutionBranchItemToAppInstitutionBranchItem(itemApi.institutionBranch)
					: undefined,
			};
		case PERSON_TYPE.SERVICE:
			return {
				item: itemApi.item,
				personType: PERSON_TYPE.SERVICE,
				ableToRepresentAs: itemApi.ableToRepresentAs,
				isInYourIntegrationServiceOffice: itemApi.isInYourIntegrationServiceOffice,
				institutionBranch: itemApi.institutionBranch
					? transformApiInstitutionBranchItemToAppInstitutionBranchItem(itemApi.institutionBranch)
					: undefined,
			};
		case PERSON_TYPE.INSTITUTION_PERSON:
			return {
				item: itemApi.item,
				personType: PERSON_TYPE.INSTITUTION_PERSON,
				contactPoints: (itemApi.contactPoints ?? []).map(tranformApiContactPointToAppContactPoint),
				ableToRepresentAs: itemApi.ableToRepresentAs,
				isInYourIntegrationServiceOffice: itemApi.isInYourIntegrationServiceOffice,
				institutionBranch: itemApi.institutionBranch
					? transformApiInstitutionBranchItemToAppInstitutionBranchItem(itemApi.institutionBranch)
					: undefined,
			};
		case PERSON_TYPE.INTEGRATION_OFFICER:
			return {
				item: itemApi.item,
				personType: PERSON_TYPE.INTEGRATION_OFFICER,
				ableToRepresentAs: itemApi.ableToRepresentAs,
				isInYourIntegrationServiceOffice: itemApi.isInYourIntegrationServiceOffice,
			};
		case PERSON_TYPE.SPONSOR:
			return {
				item: itemApi.item,
				personType: PERSON_TYPE.SPONSOR,
				ableToRepresentAs: itemApi.ableToRepresentAs,
				isInYourIntegrationServiceOffice: itemApi.isInYourIntegrationServiceOffice,
			};
		case PERSON_TYPE.WITHOUT_SPONSOR:
			return {
				item: itemApi.item,
				personType: PERSON_TYPE.WITHOUT_SPONSOR,
				ableToRepresentAs: itemApi.ableToRepresentAs,
				isInYourIntegrationServiceOffice: itemApi.isInYourIntegrationServiceOffice,
				institutionBranch: itemApi.institutionBranch
					? transformApiInstitutionBranchItemToAppInstitutionBranchItem(itemApi.institutionBranch)
					: undefined,
			};
		case PERSON_TYPE.SPONSOR_CONTACT_PERSON:
			return {
				item: itemApi.item,
				personType: PERSON_TYPE.SPONSOR_CONTACT_PERSON,
				contactPoints: (itemApi.contactPoints ?? []).map(tranformApiContactPointToAppContactPoint),
				ableToRepresentAs: itemApi.ableToRepresentAs,
				isInYourIntegrationServiceOffice: itemApi.isInYourIntegrationServiceOffice,
				institutionBranch: itemApi.institutionBranch
					? transformApiInstitutionBranchItemToAppInstitutionBranchItem(itemApi.institutionBranch)
					: undefined,
			};
		case PERSON_TYPE.INSTITUTION_BRANCH:
			return {
				item: itemApi.item,
				personType: PERSON_TYPE.INSTITUTION_BRANCH,
				ableToRepresentAs: itemApi.ableToRepresentAs,
				isInYourIntegrationServiceOffice: itemApi.isInYourIntegrationServiceOffice,
				institutionBranch: itemApi.institutionBranch
					? transformApiInstitutionBranchItemToAppInstitutionBranchItem(itemApi.institutionBranch)
					: undefined,
			};
		case PERSON_TYPE.INTEGRATION_SERVICE_OFFICE:
			return {
				item: itemApi.item,
				personType: PERSON_TYPE.INTEGRATION_SERVICE_OFFICE,
				ableToRepresentAs: itemApi.ableToRepresentAs,
				isInYourIntegrationServiceOffice: itemApi.isInYourIntegrationServiceOffice,
				institutionBranch: itemApi.institutionBranch
					? transformApiInstitutionBranchItemToAppInstitutionBranchItem(itemApi.institutionBranch)
					: undefined,
			};

		case PERSON_TYPE.ASSIGNMENT:
			return {
				item: itemApi.item,
				personType: PERSON_TYPE.ASSIGNMENT,
				ableToRepresentAs: itemApi.ableToRepresentAs,
				isInYourIntegrationServiceOffice: itemApi.isInYourIntegrationServiceOffice,
				institutionBranch: itemApi.institutionBranch
					? transformApiInstitutionBranchItemToAppInstitutionBranchItem(itemApi.institutionBranch)
					: undefined,
			};

		case PERSON_TYPE.UNKNOWN:
		default:
			return {
				item: itemApi.item,
				personType: PERSON_TYPE.UNKNOWN,
				ableToRepresentAs: itemApi.ableToRepresentAs,
				isInYourIntegrationServiceOffice: itemApi.isInYourIntegrationServiceOffice,
				institutionBranch: itemApi.institutionBranch
					? transformApiInstitutionBranchItemToAppInstitutionBranchItem(itemApi.institutionBranch)
					: undefined,
			};
	}
};
